import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AppConfigService } from 'app/core/config/app-config.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, take } from 'rxjs';
import { AdminNavigationService } from './admin-navigation.service';
import { AuthRepository, UserRole } from '../auth/auth.repository';
import { CompaniesService } from 'app/services/companies.service';
import { FeatureSettingsService } from 'app/services/feature-settings.service';

/** Maps nav item id → feature flag that must be enabled to show the item. */
const NAV_FEATURE_GATES: Record<string, string> = {
  // Top-level items
  'supervisor-classroom':      'feature_classroom',
  'reports':                   'feature_reports',
  // Settings children
  'settings.vclass-admin':     'feature_classroom',
  // Course Admin children
  'course-admin.badges':       'feature_badges',
  'course-admin.study-matrix': 'feature_study_matrix',
  'course-admin.groups':       'feature_student_groups',
};

/**
 * Nav items that are only visible to SuperUser / SysAdmin.
 * CourseAdmin, Supervisor, Advisor, and Translator must not see these.
 */
const NAV_SUPERADMIN_ONLY = new Set([
  'settings.companies',        // "Companies" list
  'settings.translators',
  'settings.permissions',
]);

@Injectable({providedIn: 'root'})
export class NavigationService
{
  readonly appConfig = inject(AppConfigService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _adminNavigationService: AdminNavigationService,
        private _authRepository: AuthRepository,
        private _companiesService: CompaniesService,
        private _featureSettingsService: FeatureSettingsService
    )
    {
    }

    /**
     * Whether "Sales" is shown: the global e-commerce switch must be on. SuperUser/SysAdmin
     * then qualify so they can reach sales across companies; others need their company on.
     */
    private _isWebsiteSalesEnabled(): boolean {
        if (this._adminNavigationService.isSuperOrSysAdmin()) {
            return this._featureSettingsService.isWebsiteSalesAllowed();
        }
        return this._featureSettingsService.isWebsiteSalesEnabled();
    }

    /**
     * Whether to show "System Settings" (/system-settings). Global admins get every
     * company; CourseAdmin and Supervisor land on their own company's settings,
     * matching the avatar menu's "Company Settings" item.
     */
    private _canViewSystemSettings(): boolean {
        if (this._adminNavigationService.isSuperOrSysAdmin()) return true;
        const role = this._authRepository.getUser()?.role;
        return role === UserRole.CourseAdmin || role === UserRole.Supervisor;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) =>
            {
                // Update admin navigation visibility based on user role
                if (this._authRepository.getUser()?.role === UserRole.Translator) {
                    navigation.admin = [
                        { id: 'dashboard', title: 'Dashboard', type: 'basic', icon: 'heroicons_outline:home', link: '/home' },
                        { id: 'translate', title: 'Translate', type: 'basic', icon: 'heroicons_outline:language', link: '/translate' },
                    ];
                } else if (this._adminNavigationService.hasAdminRole()) {
                    // Ensure admin navigation is available
                    if (!navigation.admin) {
                        navigation.admin = [];
                    }
                } else {
                    // Clear admin navigation for non-admin users
                    navigation.admin = [];
                }

                // Remove nav items (and their children) whose feature flag is disabled,
                // or whose role requirement is not met.
                // Runs recursively so child items (e.g. Course Admin sub-items) are also filtered.
                const isSuperAdmin = this._adminNavigationService.isSuperOrSysAdmin();
                const isSupervisor = this._authRepository.getUser()?.role === UserRole.Supervisor;
                const filter = (items: FuseNavigationItem[]): FuseNavigationItem[] =>
                    items
                        .filter(item => {
                            if (isSupervisor && item.id?.startsWith('settings.')
                                && item.id !== 'settings.system-settings') return false;
                            const featureKey = NAV_FEATURE_GATES[item.id];
                            if (featureKey !== undefined && !this._featureSettingsService.isFeatureEnabled(featureKey)) return false;
                            if (NAV_SUPERADMIN_ONLY.has(item.id) && !isSuperAdmin) return false;
                            if (item.id === 'settings.system-settings' && !this._canViewSystemSettings()) return false;
                            if ((item.id === 'settings.system' || item.id === 'settings.permissions')
                                && this._authRepository.getUser()?.role !== UserRole.SuperUser) return false;
                            // "Course Creation" (the SSO jump into the legacy PHP editor) is SuperUser only.
                            if (item.id === 'course-admin.legacy-creation'
                                && this._authRepository.getUser()?.role !== UserRole.SuperUser) return false;
                            // "Sales" requires the per-company Website Sales feature to be enabled.
                            if (item.id === 'sales' && !this._isWebsiteSalesEnabled()) return false;
                            // "My purchases" is for students of a company that sells online.
                            if (item.id === 'purchases' && !(this._authRepository.getUser()?.role === UserRole.Student
                                && this._featureSettingsService.isWebsiteSalesEnabled())) return false;
                            return true;
                        })
                        .map(item => item.children?.length
                            ? { ...item, children: filter(item.children) }
                            : item
                        );

                if (navigation.compact)     { navigation.compact     = filter(navigation.compact); }
                if (navigation.admin)       { navigation.admin       = filter(navigation.admin); }
                if (navigation.default)     { navigation.default     = filter(navigation.default); }
                if (navigation.futuristic)  { navigation.futuristic  = filter(navigation.futuristic); }
                if (navigation.horizontal)  { navigation.horizontal  = filter(navigation.horizontal); }

                // Wire the "Course Creation" item to the legacy-system SSO auto-login.
                // The mock-api defines it without a link/function, so it would otherwise be inert.
                const wireLegacyAction = (items: FuseNavigationItem[]): void => {
                    for (const item of items) {
                        if (item.id === 'course-admin.legacy-creation') {
                            item.function = () => this.openLegacyCourseEditor();
                        }
                        if (item.children?.length) { wireLegacyAction(item.children); }
                    }
                };
                if (navigation.compact)     { wireLegacyAction(navigation.compact); }
                if (navigation.admin)       { wireLegacyAction(navigation.admin); }
                if (navigation.default)     { wireLegacyAction(navigation.default); }
                if (navigation.futuristic)  { wireLegacyAction(navigation.futuristic); }
                if (navigation.horizontal)  { wireLegacyAction(navigation.horizontal); }

                this._navigation.next(navigation);
            }),
        );
    }

    /**
     * Open the legacy course editor with an SSO auto-login.
     *
     * When a company is in scope, log in AS that company's course-admin account by
     * resolving its impersonation triple (uid/fid/role) from the backend and POSTing it
     * to the legacy `belogin.php`. If no company is selected or the lookup fails, fall
     * back to the current user's own credentials so global admins still get in. Either
     * way a `redirect` lands the user directly on the course editor.
     */
    private openLegacyCourseEditor(): void {
        const companyId = this._companiesService.getEffectiveCompanyId();

        if (companyId) {
            this._companiesService.getLegacyCourseAdminSso(companyId)
                .pipe(take(1))
                .subscribe(account => {
                    if (account) {
                        this._submitLegacyForm(account.uid, account.fid, account.role);
                    } else {
                        this._submitLegacyFormAsCurrentUser();
                    }
                });
            return;
        }

        this._submitLegacyFormAsCurrentUser();
    }

    /** SSO into the legacy course editor using the current user's own credentials. */
    private _submitLegacyFormAsCurrentUser(): void {
        const user = this._authRepository.getUser();
        this._submitLegacyForm(
            Number(user?.user_id ?? 0),
            Number(user?.person_id ?? 0),
            Number(user?.role ?? 0)
        );
    }

    /**
     * Build and submit a hidden form POSTing the given impersonation triple to the legacy
     * `belogin.php`, redirecting to the course editor in a new tab. The legacy base URL
     * comes from the `otrainingsystem` setting.
     */
    private _submitLegacyForm(uid: number, fid: number, role: number): void {
        const base  = String(this.appConfig.get('otrainingsystem') || '').replace(/\/+$/, '');
        const token = this._authRepository.getAccessToken();

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${base}/belogin.php`;
        form.target = '_blank';
        form.style.display = 'none';

        const addField = (name: string, value: string): void => {
            const input = document.createElement('input');
            input.type  = 'hidden';
            input.name  = name;
            input.value = value;
            form.appendChild(input);
        };

        addField('Authorization', token ?? '');
        addField('uid', '' + uid);
        addField('fid', '' + fid);
        addField('role', '' + role);
        addField('redirect', 'course-editor.php');

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }
}
